import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { BarStack, Bar } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';

import {
  getParams,
  getMonth,
  thousandFormat,
  getMonths,
  financialMonth,
  getFinancialIndex,
  getMonthForPoeplePage,
  printFormat
} from '../../../../Utils/Functions';
import {
  enMonths,
  positiveActiveColor,
  negativeActiveColor,
  negativeDisableColor,
  activeLabelColor,
} from '../../../../Assets/js/constant';
import appdata from '../../../../Services/appdata';

// accessors
const x = (d) => d.month;

let tooltipTimeout;
let humantooltipTimeout;

const TopChart = ({
  summaryData,
  selectedYears,
  selectedMonths,
  selectedTopItems,
  defaultStartMonth,
  defaultMonth,
  defaultYear,
  handleFilter,
  getPeopleDetail,
}) => {
  const { tooltipOpen, tooltipTop, tooltipLeft, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const svgRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [hoverHuman, setHoverHuman] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState([]);
  const [forcasts, setForcasts] = useState([]);
  const [colors, setColors] = useState([]);
  const [dragFlag, setDragFlag] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [iX, setIX] = useState(0);
  const [iY, setIY] = useState(0);
  const [rX, setRX] = useState(0);
  const [rY, setRY] = useState(0);
  const [rW, setRW] = useState(0);
  const [rH, setRH] = useState(0);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  const prepareData = useCallback(() => {
    let dictData = {};
    let dictTotals = {};
    let dictForcasts = {};
    let data = [];
    let totals = [];
    let forcasts = [];
    let summaryMonths = [];
    let monthArray = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    let nullMonths = [];

    let colors = selectedYears.reduce((ret, year) => {
      ret.push(positiveActiveColor);
      return ret;
    }, []);

    let fiscalMonths = getMonths(defaultStartMonth);

    summaryData?.forEach((item) => {
      let month = getMonth(item.Date);
      summaryMonths.push(month);
      let boundaryMonth = financialMonth(defaultMonth, defaultStartMonth);
      let monthIndex = getFinancialIndex(
        month + 1,
        defaultStartMonth,
      );
      let boundaryIndex = getFinancialIndex(
        boundaryMonth,
        defaultStartMonth,
      );

      const useForecast =
        item.UseForecastFallback === true ||
        (boundaryIndex < monthIndex && item.FY === defaultYear) ||
        item.FY > defaultYear;

      let key;
      key = useForecast ? 'feature' : 'past';
      key = key + '_' + item.WorkDaysInPeriod + '_' + month;
      if (dictData[key] === undefined) {
        dictData[key] = {};
        dictTotals[key] = 0;
        dictForcasts[key] = 0;
        dictData[key]['value'] = {};
      }
      if (dictData[key][item.FY] === undefined) {
        dictData[key][item.FY] = 0;
        dictData[key]['month'] = month;
      }

      if (dictData[key]['value'][item.FY] === undefined)
        dictData[key]['value'][item.FY] = item;

      if (useForecast) {
        dictData[key]['future'] = true;
        dictData[key][item.FY] += parseFloat(item.ForecastFees);
      } else {
        dictData[key]['future'] = false;
        dictData[key][item.FY] += parseFloat(item.ActualFees);
      }

      dictTotals[key] += parseFloat(dictData[key][item.FY]);
      dictForcasts[key] += parseFloat(item.ForecastFees);
    });

    nullMonths = monthArray.filter((month) => !summaryMonths.includes(month));

    fiscalMonths.forEach((month) => {
      let duplicatedMonth = -1;

      Object.keys(dictData).forEach((inx) => {
        if (
          dictData[inx]['month'] === month &&
          dictData[inx]['future'] === false
        ) {
          if (duplicatedMonth === month) {
            dictData[inx]['month'] = dictData[inx]['month'];
          } else {
            dictData[inx]['month'] = dictData[inx]['month'];
            duplicatedMonth = month;
          }
          data.push(dictData[inx]);
        }

        if (
          dictData[inx]['month'] === month &&
          dictData[inx]['future'] === true
        ) {
          if (duplicatedMonth === month) {
            dictData[inx]['month'] = (dictData[inx]['month']) * -1;
          } else {
            dictData[inx]['month'] = (dictData[inx]['month']) * -1;
            duplicatedMonth = month;
          }
          data.push(dictData[inx]);
        }
      });
    });

    nullMonths.forEach((month) => {
      const nullData = {
        [selectedYears[0]]: 0,
        future: true,
        month: -100 - month,
        value: {
          [selectedYears[0]]: {
            ActualFees: '0',
            ActualFullTimeEmployees: 0,
            DOWInMonth: 0,
            Date: new Date(selectedYears[0], month, 1),
            FY: selectedYears[0],
            ForecastFees: '',
            ForecastRequiredFullTimeEmployees: 0,
            MagicNumber: 0,
            OrgName: '',
            WorkDaysInPeriod: 0,
            UseForecastFallback: true,
          },
        },
      };
      // `month` is zero-based while defaultStartMonth is one-based.
      // Use one fiscal index for every month so a missing July in a July-start
      // financial year is inserted at the start rather than appended at the end.
      const fiscalIndex =
        (month - (defaultStartMonth - 1) + 12) % 12;
      data.splice(fiscalIndex, 0, nullData);
    });

    Object.keys(dictData).forEach((inx) => {
      totals.push(dictTotals[inx]);
      forcasts.push(dictForcasts[inx]);
    });

    setData(data);
    setTotals(totals);
    setForcasts(forcasts);
    setColors(colors);
  }, [summaryData, selectedYears, defaultYear, defaultMonth, defaultStartMonth]);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    prepareData();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize, prepareData]);

  useEffect(() => {
    prepareData();
  }, [summaryData, prepareData]);

  const getColor = useCallback((bar) => {
    const year = bar.key;
    const month = bar.bar.data.month;

    if (selectedTopItems.length === 0 && selectedMonths.length === 0) {
      return negativeActiveColor;
    }
    if (selectedMonths.indexOf(month) > -1) return negativeActiveColor;

    for (let i = 0; i < selectedTopItems.length; i++) {
      if (
        selectedTopItems[i].month === month &&
        selectedTopItems[i].year === year
      ) {
        return negativeActiveColor;
      }
    }

    return negativeDisableColor;
  }, [selectedMonths, selectedTopItems]);

  const handleBar = useCallback((event, bar) => {
    if (dragFlag) return;
    let _selectedBars, _selectedMonths, _selectedTopItems;

    const month = bar.bar.data.month;
    const year = bar.key;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedTopItems = selectedTopItems.slice();
      _selectedMonths = selectedMonths.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedMonths.push(month);
        _selectedTopItems.push({ month: month, year: year });
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedTopItems.splice(index, 1);
        _selectedMonths.splice(index, 1);
      }
    } else {
      _selectedMonths = [];
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedTopItems = [];
        _selectedBars = [];
        _selectedMonths = [];
      } else {
        _selectedTopItems = [{ month: month, year: year }];
        _selectedBars = [event.target];
        _selectedMonths = [month];
        event.target.classList.add('barActive');
      }
    }

    let dimDate = [];
    _selectedTopItems.forEach((item) => {
      let _DimDate = printFormat.dateYYYYMMDD(
        getMonthForPoeplePage(item.month) + 1 >= defaultStartMonth
          ? item.year - 1
          : item.year,
        getMonthForPoeplePage(item.month) + 1,
      );
      dimDate.push(_DimDate);
      appdata.setLocalItem('peoDimDate', dimDate);
    });

    if (dimDate.length > 0) getPeopleDetail(dimDate);

    setSelectedBars(_selectedBars);

    handleFilter({
      selectedTopItems: _selectedTopItems,
      selectedMonths: _selectedMonths,
    });
  }, [selectedBars, selectedMonths, selectedTopItems, defaultStartMonth, getPeopleDetail, handleFilter, dragFlag]);

  const handleLabel = useCallback((event, month, all = '') => {
    if (dragFlag) return;
    let _selectedMonths, _selectedTopItems, _selectedBars;

    let index = NaN;
    for (let i = 0; i < selectedMonths.length; i++) {
      if (selectedMonths[i] === month) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedMonths = selectedMonths.slice();
      _selectedTopItems = selectedTopItems.slice();

      if (isNaN(index)) {
        _selectedMonths.push(month);
        _selectedTopItems.push({ month: month, year: selectedYears[0] });
      } else {
        _selectedMonths.splice(index, 1);
      }
    } else {
      _selectedBars = [];
      _selectedTopItems = [];

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
      });

      if (!isNaN(index) && selectedMonths.length === 1) {
        _selectedMonths = [];
      } else {
        _selectedMonths = [month];
        _selectedTopItems.push({ month: month, year: selectedYears[0] });
      }
    }

    setSelectedBars(_selectedBars);

    if (all !== '') {
      _selectedMonths = [];
      _selectedTopItems = [];
      data.forEach((item) => {
        _selectedMonths.push(item.month);
        _selectedTopItems.push({ month: item.month, year: selectedYears[0] });
      });
    }

    let dimDate = [];

    _selectedTopItems.forEach((item) => {
      let _DimDate = printFormat.dateYYYYMMDD(
        getMonthForPoeplePage(item.month) + 1 >= defaultStartMonth
          ? item.year - 1
          : item.year,
        getMonthForPoeplePage(item.month) + 1,
      );
      dimDate.push(_DimDate);
      appdata.setLocalItem('peoDimDate', dimDate);
    });

    if (dimDate.length > 0) getPeopleDetail(dimDate);

    handleFilter({
      selectedMonths: _selectedMonths,
      selectedTopItems: _selectedTopItems,
    });
  }, [selectedBars, selectedMonths, selectedTopItems, selectedYears, data, defaultStartMonth, getPeopleDetail, handleFilter, dragFlag]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    setHoverBar(null);
    setSelectedBars([]);
    localStorage.removeItem('peoSelectedTopItems');
    handleFilter({
      selectedMonths: [],
      selectedTopItems: [],
    });
  }, [hoverBar, selectedBars, handleFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let data = {},
      top,
      left;

    top = event.clientY - 100;
    left =
      window.innerWidth / 2 < event.clientX
        ? event.clientX - 170
        : event.clientX + 30;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
      data = bar;
    } else {
      data['month'] = bar;
    }

    data['isBar'] = isBar;

    showTooltip({
      tooltipData: data,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  const width = window.innerWidth;
  const height = (window.innerHeight - 100) / 3;
  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: 70,
  };

  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );

  let minY = Math.min(Math.min(...totals), Math.min(...forcasts));
  minY = Math.min(0, minY);
  let maxY = Math.max(Math.max(...totals), Math.max(...forcasts));
  maxY = Math.max(0, maxY);

  // scales
  const xScale = scaleBand()
    .domain(data.map(x))
    .rangeRound([0, xMax])
    .padding(0.2);

  const yScale = scaleLinear({
    domain: [minY * 1.1, maxY * 1.1],
    range: [yMax, 0],
    nice: true,
  });

  const color = scaleOrdinal({
    domain: selectedYears,
    range: colors,
  });

  let futureWidth,
    futureX = 0;
  let existPastMonth = false;
  // find first future month
  data.forEach((item) => {
    if (item.future === true && futureX === 0) {
      futureX =
        xScale(item.month) - (xScale.paddingInner() * xScale.step()) / 2;
      futureWidth = xMax - futureX;
    }
    if (item.future === false) {
      existPastMonth = true;
    }
  });

  const getLocalCoords = (event) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left - margin.left,
      y: event.clientY - rect.top - margin.top,
    };
  };

  const onSvgMouseDown = (event) => {
    if (event.button !== 0 || !svgRef.current) return;
    const target = event.target;
    if (
      target &&
      (target.tagName === 'text' ||
        target.getAttribute('data-no-drag') === 'true' ||
        (target.closest && target.closest('[data-no-drag="true"]')))
    ) {
      return;
    }
    setIsMouseDown(true);
    const pos = getLocalCoords(event);
    dragStartRef.current = { x: pos.x, y: pos.y };
    setIX(pos.x);
    setIY(pos.y);
    setRX(pos.x);
    setRY(pos.y);
    setRW(0);
    setRH(0);
    setDragFlag(true);
  };

  const onSvgMouseMove = (event) => {
    if (!isMouseDown || !dragFlag || event.buttons !== 1) return;
    const pos = getLocalCoords(event);
    const start = dragStartRef.current;
    setRX(Math.min(start.x, pos.x));
    setRY(Math.min(start.y, pos.y));
    setRW(Math.abs(start.x - pos.x));
    setRH(Math.abs(start.y - pos.y));
  };

  const onSvgMouseUp = () => {
    setIsMouseDown(false);
    if (!dragFlag) return;

    const rectLeft = Math.min(rX, rX + rW);
    const rectRight = Math.max(rX, rX + rW);
    const dragThreshold = 5;
    const didDrag = rW > dragThreshold || rH > dragThreshold;

    if (didDrag && data.length > 0) {
      const selectedData = data.filter((item) => {
        const bandLeft = xScale(item.month) ?? 0;
        const bw = xScale.bandwidth();
        const bandRight = bandLeft + bw;
        return bandLeft < rectRight && bandRight > rectLeft;
      });
      const _selectedMonths = selectedData.map((d) => d.month);
      const _selectedTopItems = selectedData.map((d) => ({
        month: d.month,
        year: selectedYears[0],
      }));
      const dimDate = [];
      _selectedTopItems.forEach((item) => {
        const _DimDate = printFormat.dateYYYYMMDD(
          getMonthForPoeplePage(item.month) + 1 >= defaultStartMonth
            ? item.year - 1
            : item.year,
          getMonthForPoeplePage(item.month) + 1,
        );
        dimDate.push(_DimDate);
        appdata.setLocalItem('peoDimDate', dimDate);
      });
      if (dimDate.length > 0) getPeopleDetail(dimDate);
      selectedBars.forEach((bar) => bar.classList.remove('barActive'));
      setSelectedBars([]);
      handleFilter({
        selectedTopItems: _selectedTopItems,
        selectedMonths: _selectedMonths,
      });
    } else if (!didDrag) {
      deSelectAll();
    }

    setIX(0);
    setIY(0);
    setRX(0);
    setRY(0);
    setRW(0);
    setRH(0);
    setDragFlag(false);
  };

  const onSvgMouseLeave = () => {
    setIsMouseDown(false);
  };

  return (
    <div>
      <div className="flex">
        <div className="subtitle mb-10">Requirements</div>
        <div>
          <svg width={400} height={30}>
            <Group top={0} left={20}>
              <Bar
                width={10}
                height={10}
                x={10}
                y={10}
                fill="grey"
                stroke="grey"
                strokeWidth={0.5}
              />
              <text fontSize={12} x={40} y={20} textAnchor="middle">
                Fees
              </text>
              <Group top={0} left={60}>
                <g transform="scale(0.021  0.021)" fill="#1c75bc">
                  <path d="M576 96c0 53.019-42.981 96-96 96s-96-42.981-96-96c0-53.019 42.981-96 96-96s96 42.981 96 96z"></path>
                  <path d="M576 256h-192c-35.346 0-64 28.654-64 64v320h64v384h80v-384h32v384h80v-384h64v-320c0-35.346-28.652-64-64-64z"></path>
                </g>
              </Group>
              <text fontSize={12} x={120} y={20} textAnchor="middle">
                Actual People
              </text>
              <Group top={0} left={170}>
                <g transform="scale(0.021  0.021)" fill="black">
                  <path d="M576 96c0 53.019-42.981 96-96 96s-96-42.981-96-96c0-53.019 42.981-96 96-96s96 42.981 96 96z"></path>
                  <path d="M576 256h-192c-35.346 0-64 28.654-64 64v320h64v384h80v-384h32v384h80v-384h64v-320c0-35.346-28.652-64-64-64z"></path>
                </g>
              </Group>
              <text fontSize={12} x={280} y={20} textAnchor="middle">
                Forecast People Based on Fees
              </text>
            </Group>
          </svg>
        </div>
      </div>
      <Grid container>
        <svg
          ref={svgRef}
          width={width}
          height={height}
          onMouseDown={onSvgMouseDown}
          onMouseMove={onSvgMouseMove}
          onMouseUp={onSvgMouseUp}
          onMouseLeave={onSvgMouseLeave}
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        >
          <Group top={margin.top} left={margin.left}>
            {futureX && existPastMonth > 0 ? (
              <Group>
                <Bar
                  width={futureWidth}
                  height={height}
                  x={futureX}
                  y={0}
                  fill="#f3f3f3"
                  stroke="black"
                  strokeWidth={0}
                />
                <text
                  fill="black"
                  dx={futureX / 2 - 5}
                  dy={12}
                  fontSize="11px"
                  fontWeight="bold"
                >
                  Past
                </text>
                <text
                  fill="black"
                  dx={futureX + futureWidth / 2 - 30}
                  dy={12}
                  fontSize="11px"
                  fontWeight="bold"
                >
                  Future
                </text>
              </Group>
            ) : null}

            <rect
              x={0}
              y={0}
              width={width}
              height={height}
              fill="transparent"
            />

            <AxisLeft
              numTicks={4}
              scale={yScale}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
                dx: '-0.33em',
                style: {
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                },
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps} fill="black" style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}>
                  ${formattedValue}
                </text>
              )}
            />

            <BarStack
              data={data}
              keys={selectedYears}
              x={x}
              xScale={xScale}
              yScale={yScale}
              color={color}
            >
              {(barStacks) => {
                return barStacks.map((barStack) => {
                  return barStack.bars.map((bar) => {
                    return (
                      <rect
                        key={`bar-income-${barStack.index}-${bar.index}`}
                        x={bar.x}
                        y={bar.y}
                        height={Math.max(0, isNaN(bar.height) ? 0 : bar.height)}
                        width={bar.width}
                        fill={getColor(bar)}
                        data-no-drag="true"
                        onClick={(event) => handleBar(event, bar)}
                        onMouseLeave={hideTooltipHandler}
                        onMouseMove={(event) =>
                          showTooltipHandler(event, xScale, bar)
                        }
                        onTouchEnd={hideTooltipHandler}
                        onTouchMove={(event) =>
                          showTooltipHandler(event, xScale, bar)
                        }
                      />
                    );
                  });
                });
              }}
            </BarStack>

            {data.map((item, index) => {
              let x = 0;
              x =
                xScale(item.month) -
                (xScale.step() * xScale.paddingInner()) / 2;

              return (
                <rect
                  key={`label-${index}`}
                  x={x}
                  y={yMax}
                  width={xScale.step()}
                  height={margin.bottom}
                  fill="transparent"
                  data-no-drag="true"
                  onClick={(event) => handleLabel(event, item.month)}
                />
              );
            })}

            {data.map((item, index) => {
              let workTime = 0;
              Object.keys(item.value).forEach((subitemkey) => {
                // Use the same actual/forecast decision as the fee bar. A missing
                // reporting month can be supplied from BFC, so its people count
                // must also be calculated from forecast fees rather than zero FTE.
                if (item.future === true) {
                  if (
                    item.value[subitemkey].ForecastFees &&
                    item.value[subitemkey].DOWInMonth &&
                    item.value[subitemkey].MagicNumber
                  ) {
                    workTime +=
                      parseFloat(item.value[subitemkey].ForecastFees) /
                      (7.6 * item.value[subitemkey].DOWInMonth) /
                      item.value[subitemkey].MagicNumber;
                  }
                } else if (item.value[subitemkey].ActualFullTimeEmployees) {
                  workTime += parseFloat(
                    item.value[subitemkey].ActualFullTimeEmployees,
                  );
                }
              });

              let left = xScale(item.month);
              let width =
                xScale.step() - (xScale.paddingInner() * xScale.step()) / 2;
              let iconRatioX = 0.03;
              let iconRatioY = 0.03;

              return (
                <Group
                  key={`human-${index}`}
                  top={yMax - 70}
                  left={left + (width * 1) / 2 - 20}
                >
                  <g
                    data-no-drag="true"
                    transform={`scale(${iconRatioX}  ${iconRatioY})`}
                    fill={
                      item.future === false
                        ? tooltipOpen &&
                          tooltipData.isBar === false &&
                          tooltipData.month === item.month
                          ? '#0c058c'
                          : '#1c75bc'
                        : tooltipOpen &&
                          tooltipData.isBar === false &&
                          tooltipData.month === item.month
                        ? 'grey'
                        : 'black'
                    }
                    onMouseLeave={hideTooltipHandler}
                    onMouseMove={(event) =>
                      showTooltipHandler(event, null, item.month, false)
                    }
                    onTouchEnd={hideTooltipHandler}
                    onTouchMove={(event) =>
                      showTooltipHandler(event, null, item.month, false)
                    }
                    onClick={(event) => handleLabel(event, item.month)}
                  >
                    <path d="M576 96c0 53.019-42.981 96-96 96s-96-42.981-96-96c0-53.019 42.981-96 96-96s96 42.981 96 96z"></path>
                    <path d="M576 256h-192c-35.346 0-64 28.654-64 64v320h64v384h80v-384h32v384h80v-384h64v-320c0-35.346-28.652-64-64-64z"></path>
                  </g>
                  <text
                    key={index}
                    fontSize={11}
                    x={15}
                    y={45}
                    textAnchor="middle"
                    style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
                  >
                    {item.future
                      ? Number(workTime.toFixed(1))
                      : workTime === 0
                      ? ''
                      : Number(workTime.toFixed(1))}
                  </text>
                </Group>
              );
            })}

            <AxisBottom
              hideTicks={true}
              scale={xScale}
              top={yMax}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fontSize: 11,
                textAnchor: 'middle',
                dy: '-0.5em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  fill={
                    selectedMonths.indexOf(parseInt(formattedValue)) > -1
                      ? activeLabelColor
                      : 'black'
                  }
                  style={{
                    fontWeight:
                      selectedMonths.indexOf(parseInt(formattedValue)) > -1 ? 700 : 500,
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                  }}
                  onClick={(event) =>
                    handleLabel(event, parseInt(formattedValue))
                  }
                >
                  {enMonths[getMonthForPoeplePage(formattedValue)]}
                </text>
              )}
            />

            {dragFlag && (rW > 0 || rH > 0) ? (
              <rect
                x={rX}
                y={rY}
                width={rW}
                height={rH}
                fill="lightblue"
                fillOpacity={0.2}
                stroke="#00f3ff"
                strokeWidth={1}
                pointerEvents="none"
              />
            ) : null}
          </Group>
        </svg>

        {tooltipOpen && tooltipData.isBar === true && (
          <TooltipWithBounds
           
            top={tooltipTop + pageYOffset}
            left={tooltipLeft}
          >
            {tooltipData.isBar ? (
              <div className="bkg-white color-black">
                <div className="pdv-10">
                  <strong>
                    FY {tooltipData.key} : {enMonths[x(tooltipData.bar.data)]}
                  </strong>
                </div>

                <div className="ft-12">
                  Income:{' '}
                  <strong>
                    $
                    {thousandFormat(
                      tooltipData.bar.data.value[tooltipData.key]?.ActualFees || 0,
                    ) !== '0'
                      ? thousandFormat(
                          tooltipData.bar.data.value[tooltipData.key]
                            .ActualFees,
                        )
                      : thousandFormat(
                          tooltipData.bar.data.value[tooltipData.key]
                            .ForecastFees,
                        )}
                  </strong>
                </div>
              </div>
            ) : (
              <div className="bkg-white color-black">empty</div>
            )}
          </TooltipWithBounds>
        )}
      </Grid>
    </div>
  );
};

TopChart.propTypes = {
  summaryData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultYear: PropTypes.number.isRequired,
  handleFilter: PropTypes.func.isRequired,
  getPeopleDetail: PropTypes.func.isRequired,
};

export default TopChart;
